import * as Sentry from "@sentry/react";
// biome-ignore lint/style/noRestrictedImports: this is ok in the wrapped file
import type { Router } from "@tanstack/react-router";
import { isLocal, isProd } from "../utils/envs.front.ts";
import { type ContextDataArgs, processContextData } from "./sentry/sentry-context-data.ts";

interface InitSentryParams {
  env: string;
  release: string;
  // oxlint-disable-next-line no-explicit-any
  router: Router<any, any, any>;
}

export const initSentry = ({ env, release, router }: InitSentryParams) => {
  Sentry.init({
    enableLogs: true,
    dsn: "https://1df3a5d696df9da7da3e343836e808a4@o4508286183735296.ingest.de.sentry.io/4508286187143248",
    release,
    integrations: [
      Sentry.consoleLoggingIntegration({ levels: ["log", "warn", "error"] }),
      Sentry.globalHandlersIntegration({ onerror: true, onunhandledrejection: true }),
      Sentry.tanstackRouterBrowserTracingIntegration(router),
      Sentry.browserApiErrorsIntegration({
        setTimeout: true,
        setInterval: true,
        requestAnimationFrame: true,
        XMLHttpRequest: true,
        eventTarget: true,
        unregisterOriginalCallbacks: true,
      }),
    ],
    environment: env,
    tracesSampleRate: env === "production" ? 0.2 : 0,
    ignoreSpans: [{ op: /^resource\./ }],
    // Replays
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    tracePropagationTargets: [
      "https://api.grafit.pro",
      /https:\/\/preview-pr-\d+--grafit-api\.netlify\.app/,
      "https://api.grafit.fr",
      /https:\/\/\d+\.api-dev\.grafit\.fr/,
    ],
  });

  // Replay (~120 KB) only buffers for on-error sampling, so it can attach after startup.
  // Same pattern as initPosthog: handlers on import(), no destructuring, so a failed load stays local
  // (no `vite:preloadError` page reload, no unhandled rejection).
  void import("@sentry-internal/replay").then(
    (module) => Sentry.addIntegration(module.replayIntegration({ maskAllText: false, maskAllInputs: false })),
    () => undefined,
  );
};

interface SentryUser {
  id: string;
  email?: string;
}

export const setSentryUser = (user: SentryUser | null) => {
  Sentry.setUser(user);
};

export const setSentryOrganisation = (organisationId: string | null) => {
  Sentry.setTag("organisationId", organisationId ?? undefined);
};

export const setSentryProject = (projectId: string | null) => {
  Sentry.setTag("projectId", projectId ?? undefined);
};

export const captureReactException = Sentry.captureReactException;

export const ErrorBoundary = Sentry.ErrorBoundary;

interface LogIssueParams extends Omit<ContextDataArgs, "message"> {
  error: Error;
  context?: ContextDataArgs["context"] & { level?: never };
}

export function addContext(name: string, context: Record<string, unknown>) {
  Sentry.setContext(name, context);
}

export function log(
  message: string,
  level: "trace" | "debug" | "info" | "warn" | "error" = "info",
  attributes?: Record<string, unknown>,
): void {
  Sentry.logger[level](message, attributes);
  if (isLocal()) {
    console[level](message, attributes);
  }
}

export const alertDevs = ({ error, exception, context = {} }: LogIssueParams) => {
  // captureException drops custom error properties, so serialize the reported error here too.
  const { extra, attachments } = processContextData({
    message: error.message,
    exception,
    context: { ...context, reportedError: error },
  });

  if (!isProd()) {
    // oxlint-disable-next-line no-console
    console.error(`🚨 [ISSUE] ${error.message} (attachmentsLength: ${attachments.length})`, extra);
  }

  if (isLocal()) {
    // Don't polute sentry with errors
    return;
  }

  Sentry.withScope((scope) => {
    for (const attachment of attachments) {
      scope.addAttachment(attachment);
    }
    scope.setExtras(extra);

    // error.name is what Sentry displays at the top level in the UI, and uses it to group errors by default
    // error.message is the subtitle of the error in the UI
    error.name = error.name === "Error" ? error.message : error.name;

    Sentry.captureException(error);
  });
};

export function setFeatureFlagsContext(featureFlags: { id: string; enabled: boolean }[]) {
  const featureFlagsRecord = featureFlags.reduce<Record<string, boolean>>((acc, featureFlag) => {
    acc[featureFlag.id] = featureFlag.enabled;
    return acc;
  }, {});

  Sentry.setContext("featureFlags", featureFlagsRecord);
  return;
}
